import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { TokenState } from '../stores/data/types';
import Stream from './Stream';
import AnimatedCard from './AnimatedCard';
import PulseCard from './PulseCard';
import StatisticsSortSelector, { StatisticsSortType } from './StatisticsSortSelector';
import { useUIStore, FilterFormData } from '../stores/ui/uiStore';
import { useDeckFilter } from '../hooks/useDeckFilter';
import FilterIcon from './FilterIcon';
import FilterChips from './FilterChips';
import FilterModal from './FilterModal';
import feManagementSystemApi, { TokenStreamDeckFilter } from '../api/fe-management-system-api';

interface StatisticsStreamProps {
  data: TokenState[];
  filter: TokenStreamDeckFilter;
  isConnected: boolean;
  reconnectAttempts: number;
  defaultSortType?: StatisticsSortType;
  deckId: string;
  name: string;
}

// 정렬 함수들을 객체로 정의
const sortFunctions = {
  athAfCnt: (a: TokenState, b: TokenState) => b.statistics.athAfCnt - a.statistics.athAfCnt,
  currentAfCnt: (a: TokenState, b: TokenState) => b.statistics.currentAfCnt - a.statistics.currentAfCnt,
  athTokenAmountInDecimals: (a: TokenState, b: TokenState) => b.statistics.athTokenAmountInDecimals - a.statistics.athTokenAmountInDecimals,
  currentTokenAmountInDecimals: (a: TokenState, b: TokenState) => b.statistics.currentTokenAmountInDecimals - a.statistics.currentTokenAmountInDecimals,
  totalUsedUsd: (a: TokenState, b: TokenState) => b.statistics.totalUsedUsd - a.statistics.totalUsedUsd,
  atlMc: (a: TokenState, b: TokenState) => b.statistics.atlMc - a.statistics.atlMc,
  athMc: (a: TokenState, b: TokenState) => b.statistics.athMc - a.statistics.athMc,
  lastFlowMc: (a: TokenState, b: TokenState) => b.statistics.lastFlow.mc - a.statistics.lastFlow.mc,
  lastUpdated: (a: TokenState, b: TokenState) => new Date(b.statistics.lastUpdated).getTime() - new Date(a.statistics.lastUpdated).getTime(),
  firstTransferAt: (a: TokenState, b: TokenState) =>
    new Date(b.tokenInfo.firstTransferAt || 0).getTime() -
    new Date(a.tokenInfo.firstTransferAt || 0).getTime(),
  firstTransferAtAsc: (a: TokenState, b: TokenState) => {
    const aTime = new Date(a.tokenInfo.firstTransferAt || 0).getTime();
    const bTime = new Date(b.tokenInfo.firstTransferAt || 0).getTime();
    // firstTransferAt 없는 토큰(=0)은 맨 아래로 떨어지도록 Infinity 처리
    const aEffective = aTime === 0 ? Infinity : aTime;
    const bEffective = bTime === 0 ? Infinity : bTime;
    return aEffective - bEffective;
  },
  tokenAmountRatio: (a: TokenState, b: TokenState) => {
    // currentTokenAmountInDecimals / athTokenAmountInDecimals 비율 계산
    const aRatio = a.statistics.athTokenAmountInDecimals > 0 
      ? a.statistics.currentTokenAmountInDecimals / a.statistics.athTokenAmountInDecimals 
      : 0;
    const bRatio = b.statistics.athTokenAmountInDecimals > 0 
      ? b.statistics.currentTokenAmountInDecimals / b.statistics.athTokenAmountInDecimals 
      : 0;
    // 내림차순 정렬 (큰 값부터)
    return bRatio - aRatio;
  }
};



const StatisticsStream: React.FC<StatisticsStreamProps> = React.memo(({
  data,
  isConnected,
  reconnectAttempts,
  filter,
  defaultSortType = 'tokenAmountRatio',
  deckId,
}) => {
  const validSortTypes: StatisticsSortType[] = [
    'athAfCnt',
    'currentAfCnt',
    'athTokenAmountInDecimals',
    'currentTokenAmountInDecimals',
    'totalUsedUsd',
    'atlMc',
    'athMc',
    'lastFlowMc',
    'lastUpdated',
    'firstTransferAt',
    'firstTransferAtAsc',
    'tokenAmountRatio',
  ];

  const [selectedSortType, setSelectedSortType] = useState<StatisticsSortType>(() =>
    validSortTypes.includes(defaultSortType as StatisticsSortType)
      ? defaultSortType
      : 'tokenAmountRatio',
  );
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const { maxCardCount, setDeckTokenCount } = useUIStore();

  // API에서 받아온 필터를 사용하여 데이터 필터링
  const {
    filteredData,
    isFilterActive,
  } = useDeckFilter(data, filter);

  // 덱의 토큰 개수를 store에 업데이트
  useEffect(() => {
    setDeckTokenCount(deckId, filteredData.length);
  }, [deckId, filteredData.length, setDeckTokenCount]);

  const handleApplyFilter = useCallback(async (newFilters: FilterFormData | null) => {
    await feManagementSystemApi.updateTokenStreamDeck(deckId, { filter: newFilters as TokenStreamDeckFilter });
    // 페이지 새로고침
    window.location.reload();
  }, [deckId]);
  
  // 정렬된 데이터 계산
  const sortedData = useMemo(() => {
    const comparator = (a: TokenState, b: TokenState) => {
      const sortFunction = (sortFunctions as any)[selectedSortType] as
        | ((a: TokenState, b: TokenState) => number)
        | undefined;

      if (!sortFunction) return 0;
      return sortFunction(a, b);
    };

    return [...filteredData]
      .sort((a, b) => {
        return comparator(a, b);
      })
      .slice(0, maxCardCount);
  }, [
    filteredData,
    selectedSortType,
    maxCardCount,
  ]);

  const currentMcLabel = useMemo(() => {
    const mcFilter = (filter as any)?.mc as { min?: number; max?: number } | undefined;
    if (!mcFilter || (mcFilter.min === undefined && mcFilter.max === undefined)) {
      return '';
    }

    const formatK = (value: number) => {
      if (value >= 1_000_000) {
        const m = value / 1_000_000;
        const formatted = Number.isInteger(m) ? m.toFixed(0) : m.toFixed(1).replace(/\.0$/, '');
        return `${formatted}M`;
      }
      return `${(value / 1000).toFixed(0)}K`;
    };

    if (mcFilter.min !== undefined && mcFilter.max !== undefined) {
      return `${formatK(mcFilter.min)} \u2264 \ud835\udc65 \u2264 ${formatK(mcFilter.max)}`;
    }
    if (mcFilter.min !== undefined) {
      return `${formatK(mcFilter.min)} \u2264 \ud835\udc65`;
    }
    return `\ud835\udc65 \u2264 ${formatK(mcFilter.max as number)}`;
  }, [filter]);

  const getStreamState = (hasData: boolean) => {
    if (!isConnected && reconnectAttempts === 0) {
      return 'LOADING';
    }
    return hasData ? 'SYNCED' : 'EMPTY';
  };

  return (
    <>
      <div className="flex-shrink-0 w-[508px] border-r border-gray-800 pr-2">
        <div className="flex items-center px-4 justify-between mb-3">
          <div className="flex items-center space-x-2">
            {currentMcLabel ? (
              <div className="text-sm font-semibold text-gray-400">{currentMcLabel}</div>
            ) : null}
            <div className="text-sm text-gray-400">({filteredData.length})</div>
            {/* <h3 className="text-sm font-semibold text-white">{sortTypeLabels[selectedSortType]}</h3> */}
            {/* 필터 활성화 표시 - 클릭하면 모달 열림 */}
            {isFilterActive && (
              <FilterIcon
                isActive={true}
                onClick={() => setIsFilterModalOpen(true)}
                size="sm"
              />
            )}
          </div>
          <StatisticsSortSelector
            selectedType={selectedSortType}
            onTypeChange={setSelectedSortType}
          />
        </div>

        {/* 필터 칩 (읽기 전용) */}
        <FilterChips filters={filter as any} />

        <Stream
          title=""
          state={getStreamState(sortedData.length > 0)}
          className="border-gray-600"
        >
          {sortedData.map((data) => (
            <AnimatedCard key={`${data.tokenAddress}-${selectedSortType}`} id={data.tokenAddress}>
              <PulseCard
                data={data}
              />
            </AnimatedCard>
          ))}
        </Stream>
      </div>

      {/* 필터 모달 */}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilter={handleApplyFilter}
        deckId={deckId}
        currentFilters={filter as any}
      />
    </>
  );
}, (prevProps, nextProps) => {
  // StatisticsStream이 리렌더링되어야 하는 경우만 false 반환
  return (
    prevProps.data === nextProps.data && // 데이터 배열 참조가 같으면 (내부 변경은 PulseCard가 감지)
    prevProps.filter === nextProps.filter &&
    prevProps.isConnected === nextProps.isConnected &&
    prevProps.reconnectAttempts === nextProps.reconnectAttempts &&
    prevProps.deckId === nextProps.deckId &&
    prevProps.name === nextProps.name &&
    prevProps.defaultSortType === nextProps.defaultSortType
  );
});

StatisticsStream.displayName = 'StatisticsStream';

export default StatisticsStream;
